import React, { useState } from 'react';
import api from '@/lib/api';
import { formatDateNL } from '@/lib/format';
import { CalendarClock, Loader2 } from 'lucide-react';

/**
 * 1-klik contract verlengen. Schrijft uitsluitend contract_einddatum
 * (+ audit-regel server-side); status/verloning blijven ongemoeid.
 */
export function VerlengDialog({ employee, onClose, onDone }) {
  const [date, setDate] = useState(employee.contract_einddatum || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const submit = async (payload, key) => {
    setBusy(key); setError('');
    try {
      const { data } = await api.patch(`/employees/${employee.id}/contract-verlengen`, {
        ...payload, reason: reason.trim() || undefined,
      });
      onDone?.(data);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Verlengen mislukt');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="verleng-dialog">
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <CalendarClock size={16} /> Contract verlengen
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          {employee.name} · huidige einddatum{' '}
          {employee.contract_einddatum ? formatDateNL(employee.contract_einddatum) : '—'}
        </p>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => submit({ months: 6 }, '6')}
            disabled={!!busy}
            data-testid="verleng-6m"
            className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
          >
            {busy === '6' ? <Loader2 size={14} className="mx-auto animate-spin" /> : '+6 maanden'}
          </button>
          <button
            type="button"
            onClick={() => submit({ months: 12 }, '12')}
            disabled={!!busy}
            data-testid="verleng-12m"
            className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
          >
            {busy === '12' ? <Loader2 size={14} className="mx-auto animate-spin" /> : '+12 maanden'}
          </button>
        </div>

        <label className="mt-4 block text-xs font-medium text-slate-600">Of kies een datum</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="verleng-datum"
          className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        />

        <label className="mt-4 block text-xs font-medium text-slate-600">Reden/notitie (optioneel)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="Bijv. verlenging na functioneringsgesprek"
          data-testid="verleng-reden"
          className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        />
        {error && <p className="mt-2 text-xs text-red-600" data-testid="verleng-error">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="verleng-cancel"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={() => submit({ contract_einddatum: date }, 'date')}
            disabled={!date || !!busy}
            data-testid="verleng-save"
            className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
          >
            {busy === 'date' ? 'Bezig…' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
